import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';

import { ApiError, api, unwrap } from '../api/client.js';
import {
  GradeBadge,
  TileStatusIcon,
  useLocale,
  type Grade,
  type TileStatus,
} from '../components/site.js';
import { TURNSTILE_SITE_KEY, Turnstile } from '../components/Turnstile.js';
import {
  Button,
  Card,
  Checkbox,
  LinkButton,
  LoadError,
  Loading,
  Notice,
  SeverityBadge,
  TextField,
  type Severity,
} from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { toLanguage } from '../i18n.js';

/** Result pages are shared by link, not meant for search engines (US-09: noindex). */
function useNoIndex() {
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex';
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);
}

/** Public Quick-Check form (target of the landing page widget: /check?url=…). */
export function QuickCheckStart() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [url, setUrl] = useState(params.get('url') ?? '');
  const [token, setToken] = useState<string>();
  const [error, setError] = useState<string>();
  useNoIndex();
  const start = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/public/quick-checks', {
          body: { url: url.trim(), ...(token ? { turnstileToken: token } : {}) },
        }),
      ),
    onSuccess: (data) => void navigate(`/check/${data.id}`),
  });
  const onToken = useCallback((value: string) => setToken(value), []);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!url.trim()) return setError(t('errors.required'));
    setError(undefined);
    start.mutate();
  };
  const captcha = start.error instanceof ApiError && start.error.code === 'CAPTCHA_REQUIRED';
  let failure: string | undefined;
  if (start.error instanceof ApiError) {
    if (start.error.code === 'RATE_LIMITED') failure = t('quickCheck.limit');
    else if (!captcha) failure = errorMessage(start.error, t);
  } else if (start.error) failure = errorMessage(start.error, t);

  return (
    <Card>
      <h1 className="mb-2 text-2xl font-bold">{t('quickCheck.title')}</h1>
      <p className="mb-4 text-slate-800">{t('quickCheck.intro')}</p>
      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        {failure ? <Notice kind="error">{failure}</Notice> : null}
        <TextField
          label={t('addSite.url')}
          hint={t('addSite.urlHint')}
          inputMode="url"
          autoComplete="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          error={error}
        />
        {captcha ? (
          TURNSTILE_SITE_KEY ? (
            <div>
              <p className="mb-2">{t('quickCheck.captcha')}</p>
              <Turnstile onToken={onToken} language={i18n.language} />
            </div>
          ) : (
            <Notice kind="warning">{t('quickCheck.limit')}</Notice>
          )
        ) : null}
        <Button type="submit" busy={start.isPending}>
          {start.isPending ? t('addSite.checking') : t('quickCheck.submit')}
        </Button>
        <p className="text-sm text-slate-700">{t('quickCheck.privacy')}</p>
      </form>
    </Card>
  );
}

const CATEGORY_KEYS: Record<string, string> = {
  availability: 'site.tiles.availability',
  security: 'site.tiles.security',
  seo: 'site.tiles.seo',
  performance: 'site.tiles.performance',
  compliance: 'site.tiles.compliance',
};

function EmailForm({ id }: { id: string }) {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; consent?: string }>({});
  const send = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/public/quick-checks/{checkId}/email', {
          params: { path: { checkId: id } },
          body: {
            email: email.trim(),
            consent: true,
            locale: toLanguage(i18n.language),
          },
        }),
      ),
  });
  if (send.isSuccess) return <Notice kind="success">{t('quickCheck.emailSent', { email })}</Notice>;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next: typeof errors = {};
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) next.email = t('errors.email');
    if (!consent) next.consent = t('quickCheck.consentRequired');
    setErrors(next);
    if (Object.keys(next).length === 0) send.mutate();
  };
  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-3">
      {send.isError ? <Notice kind="error">{errorMessage(send.error, t)}</Notice> : null}
      <TextField
        label={t('auth.email')}
        type="email"
        autoComplete="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={errors.email}
      />
      <Checkbox
        label={t('quickCheck.consent')}
        checked={consent}
        onChange={(e) => setConsent(e.target.checked)}
        error={errors.consent}
      />
      <div>
        <Button type="submit" variant="secondary" busy={send.isPending}>
          {t('quickCheck.emailSubmit')}
        </Button>
      </div>
    </form>
  );
}

/** US-09 result: score, five lights, top 3 problems, the rest after registration. */
export function QuickCheckResult() {
  const { checkId = '' } = useParams();
  const { t } = useTranslation();
  const locale = useLocale();
  useNoIndex();
  const check = useQuery({
    queryKey: ['quick-check', checkId, locale],
    queryFn: () =>
      unwrap(
        api.GET('/public/quick-checks/{checkId}', {
          params: { path: { checkId }, query: { locale } },
        }),
      ),
    refetchInterval: (query) => {
      const d = query.state.data;
      if (!d || d.status === 'queued' || d.status === 'running') return 2000;
      return d.result?.performancePending ? 5000 : false;
    },
  });
  if (check.isPending) return <Loading />;
  if (check.isError)
    return (
      <LoadError message={errorMessage(check.error, t)} onRetry={() => void check.refetch()} />
    );
  const c = check.data;
  const register = `/register?${new URLSearchParams({ url: c.host, quickCheck: c.id }).toString()}`;
  if (c.status === 'queued' || c.status === 'running')
    return (
      <Card>
        <h1 className="mb-2 text-2xl font-bold">
          {t('quickCheck.runningTitle', { host: c.host })}
        </h1>
        <p role="status" className="text-slate-800">
          {t('quickCheck.running')}
        </p>
      </Card>
    );
  if (c.status === 'failed' || !c.result)
    return (
      <Card>
        <h1 className="mb-2 text-2xl font-bold">{c.host}</h1>
        <Notice kind="error">{t('quickCheck.failed')}</Notice>
        <p className="mt-4">
          <LinkButton to="/check" variant="soft">
            {t('quickCheck.again')}
          </LinkButton>
        </p>
      </Card>
    );
  const r = c.result;
  return (
    <div className="flex flex-col gap-4">
      <Card>
        <h1 className="mb-1 text-2xl font-bold">{t('quickCheck.resultTitle', { host: c.host })}</h1>
        {!r.reachable ? <Notice kind="error">{t('quickCheck.unreachable')}</Notice> : null}
        {r.grade && r.healthScore !== null ? (
          <div className="mt-3 flex flex-wrap items-center gap-4">
            <GradeBadge grade={r.grade as Grade} score={r.healthScore} />
            <p className="text-lg font-semibold">{r.label}</p>
          </div>
        ) : null}
      </Card>
      <Card>
        <h2 className="mb-3 text-xl font-semibold">{t('quickCheck.areas')}</h2>
        <ul className="grid gap-2 sm:grid-cols-2">
          {r.categories.map((cat) => (
            <li
              key={cat.key}
              className="flex items-center justify-between gap-2 rounded-md border border-slate-200 px-3 py-2"
            >
              <span className="font-medium">{t(CATEGORY_KEYS[cat.key] ?? cat.key)}</span>
              {cat.key === 'performance' && r.performancePending ? (
                <span className="text-sm text-slate-700">{t('quickCheck.pending')}</span>
              ) : (
                <TileStatusIcon status={cat.status as TileStatus} />
              )}
            </li>
          ))}
        </ul>
      </Card>
      <Card>
        <h2 className="mb-3 text-xl font-semibold">{t('quickCheck.top')}</h2>
        {r.topFindings.length === 0 ? (
          <Notice kind="success">{t('quickCheck.noProblems')}</Notice>
        ) : null}
        <ol className="flex flex-col gap-2">
          {r.topFindings.map((f) => (
            <li key={f.ruleCode} className="flex flex-wrap items-center gap-2">
              <SeverityBadge severity={f.severity as Severity} />
              <span className="font-medium">{f.title}</span>
            </li>
          ))}
        </ol>
        {r.teaserHiddenCount > 0 ? (
          <p className="mt-3 text-slate-800">
            {t('quickCheck.more', { count: r.teaserHiddenCount })}
          </p>
        ) : null}
        <p className="mt-4">
          <Link
            to={register}
            className="inline-flex min-h-11 items-center rounded-md bg-blue-700 px-4 font-semibold text-white hover:bg-blue-800"
          >
            {t('quickCheck.cta')}
          </Link>
        </p>
        <p className="mt-2 text-sm text-slate-700">{t('quickCheck.ctaText')}</p>
      </Card>
      <Card>
        <h2 className="mb-2 text-xl font-semibold">{t('quickCheck.emailTitle')}</h2>
        <EmailForm id={c.id} />
      </Card>
    </div>
  );
}

/** Link from the double opt-in mail. */
export function QuickCheckConfirm() {
  const { checkId = '' } = useParams();
  const [params] = useSearchParams();
  const { t } = useTranslation();
  useNoIndex();
  const confirm = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/public/quick-checks/{checkId}/email/confirm', {
          params: { path: { checkId } },
          body: { token: params.get('token') ?? '' },
        }),
      ),
  });
  const { mutate } = confirm;
  // Once: the token works only one time (StrictMode runs effects twice in development).
  const sent = useRef(false);
  useEffect(() => {
    if (sent.current) return;
    sent.current = true;
    mutate();
  }, [mutate]);
  return (
    <Card>
      <h1 className="mb-3 text-2xl font-bold">{t('quickCheck.confirmTitle')}</h1>
      {confirm.isPending || confirm.isIdle ? <Loading /> : null}
      {confirm.isSuccess ? <Notice kind="success">{t('quickCheck.confirmed')}</Notice> : null}
      {confirm.isError ? <Notice kind="error">{errorMessage(confirm.error, t)}</Notice> : null}
      <p className="mt-4">
        <LinkButton to={`/check/${checkId}`} variant="soft">
          {t('quickCheck.toResult')}
        </LinkButton>
      </p>
    </Card>
  );
}
